import type {
  GovernanceAccessScope, GovernanceAccount, GovernanceAccountBalance, GovernanceApproval, GovernanceBudget,
  GovernanceGrant, GovernanceScopeChanges, GovernanceStaffPay,
} from "../../services/financialService";
import { byNumber, byText, reversed, type RegisterConfig } from "../register/registerLogic";

/**
 * Governance oversight: read-only figures for leadership, the board and
 * donors, each seeing only what their access allows; and, for whoever sets
 * up access, the board and donor logins with what each may see and why it
 * last changed. Mirrors apps/financial/governance.py.
 */

export const num = (value?: string | number | null) => Number(value || 0);
export const money = (value?: string | number | null) => `UGX ${Math.round(num(value)).toLocaleString("en-GB")}`;
export const percent = (value?: string | number | null) => `${num(value).toLocaleString("en-GB", { maximumFractionDigits: 1 })}%`;
export const shortDate = (value?: string | null) => {
  if (!value) return "";
  const [y, m, d] = value.slice(0, 10).split("-").map(Number);
  return new Date(y, m - 1, d).toLocaleDateString("en-GB", { day: "numeric", month: "short", year: "numeric" });
};
export const stamp = (value?: string | null) => (value ? `${shortDate(value)} ${value.slice(11, 16)}` : "");

type Tone = { label: string; dot: string };
const tone = (label: string, dot: string): Tone => ({ label, dot });

// ---- What an access shows ----------------------------------------------------------------------

/** In the order and words of governance.SCOPE_FLAGS. */
export const SCOPE_FLAGS = [
  ["can_view_financial_position", "Financial position", "Assets, liabilities, income and spending for the period"],
  ["can_view_budgets", "Budgets", "Approved budgets against what was actually spent"],
  ["can_view_approvals", "Approval oversight", "Who approved which financial documents, and when"],
  ["can_view_exceptions", "Exceptions", "Overdue fees, unmatched bank lines and control differences"],
  ["can_drill_down", "Account and record detail", "Account-by-account balances, budget lines and named pay"],
  ["can_view_salary", "Salary authorization", "Monthly pay totals; with detail, each person's pay"],
] as const;
export type ScopeFlag = (typeof SCOPE_FLAGS)[number][0];

export const GRANT_SCOPES: [GovernanceAccessScope["grant_scope"], string, string][] = [
  ["none", "No grants", "No grant figures at all"],
  ["selected", "Selected grants", "Only the grants ticked below"],
  ["all", "All grants", "Every approved grant"],
];
export const grantScopeLabel = (value: string) => GRANT_SCOPES.find(([key]) => key === value)?.[1] ?? value;

export const ROLE_LABELS: Record<string, string> = { board_viewer: "Board member", donor_viewer: "Donor" };
export const roleLabel = (userType: string) => ROLE_LABELS[userType] ?? userType.replaceAll("_", " ");

/** What a scope lets its holder see, as short labels. */
export function canSee(scope: Pick<GovernanceAccessScope, ScopeFlag | "grant_scope">) {
  const seen: string[] = SCOPE_FLAGS.filter(([field]) => scope[field]).map(([, label]) => label);
  if (scope.grant_scope !== "none") seen.push(grantScopeLabel(scope.grant_scope));
  return seen;
}

/** The status of a login's access; no scope means role defaults apply when they first sign in. */
export function accessStatus(account: Pick<GovernanceAccount, "is_active">, scope?: Pick<GovernanceAccessScope, "status"> | null): Tone {
  if (!account.is_active) return tone("Login turned off", "bg-gray-400");
  if (!scope) return tone("Not set up", "bg-amber-500");
  if (scope.status === "expired") return tone("Ended", "bg-red-500");
  if (scope.status === "not_started") return tone("Starts later", "bg-sky-500");
  return tone("Active", "bg-emerald-500");
}

export interface AccessRow {
  account: GovernanceAccount;
  scope?: GovernanceAccessScope;
}

/** Every board and donor login beside its access, if any. */
export function accessRows(accounts: GovernanceAccount[], scopes: GovernanceAccessScope[]): AccessRow[] {
  const byUser = new Map(scopes.map((scope) => [scope.user, scope]));
  return accounts.map((account) => ({ account, scope: byUser.get(account.id) }));
}

const period = (scope?: GovernanceAccessScope) => {
  if (!scope) return "";
  if (!scope.valid_from && !scope.valid_until) return "No end date";
  return `${scope.valid_from ? shortDate(scope.valid_from) : "Any time"} – ${scope.valid_until ? shortDate(scope.valid_until) : "no end"}`;
};
export const accessPeriod = period;

export const accessConfig: RegisterConfig<AccessRow> = {
  search: (row) => `${row.account.name} ${row.account.username} ${row.scope?.grant_names.join(" ") ?? ""}`,
  views: [
    { key: "all", label: "All" },
    { key: "active", label: "Active", match: (row) => row.account.is_active && row.scope?.status === "active" },
    { key: "unset", label: "Not set up", match: (row) => !row.scope },
    { key: "ended", label: "Ended or not started", match: (row) => Boolean(row.scope && row.scope.status !== "active") },
    { key: "salary", label: "Salary authorization", match: (row) => Boolean(row.scope?.can_view_salary) },
  ],
  category: { label: "Role", value: (row) => roleLabel(row.account.user_type) },
  sorts: [
    { key: "name", label: "Name", compare: byText((row) => row.account.name) },
    { key: "changed", label: "Changed most recently", compare: reversed(byText((row) => row.scope?.updated_at ?? "")) },
    { key: "ends", label: "Ending soonest", compare: byText((row) => row.scope?.valid_until || "9999") },
  ],
};

// ---- Changing an access ------------------------------------------------------------------------

export type ScopeDraft = Required<Pick<GovernanceAccessScope, ScopeFlag | "grant_scope" | "grants">> & { valid_from: string; valid_until: string };

export const draftOf = (scope: GovernanceAccessScope): ScopeDraft => ({
  can_view_financial_position: scope.can_view_financial_position,
  can_view_budgets: scope.can_view_budgets,
  can_view_approvals: scope.can_view_approvals,
  can_view_exceptions: scope.can_view_exceptions,
  can_drill_down: scope.can_drill_down,
  can_view_salary: scope.can_view_salary,
  grant_scope: scope.grant_scope,
  grants: [...scope.grants],
  valid_from: scope.valid_from ?? "",
  valid_until: scope.valid_until ?? "",
});

/** Only the fields that differ, as the server takes them. */
export function changedFields(scope: GovernanceAccessScope, draft: ScopeDraft): GovernanceScopeChanges {
  const out: GovernanceScopeChanges = {};
  for (const [field] of SCOPE_FLAGS) if (draft[field] !== scope[field]) out[field] = draft[field];
  if (draft.grant_scope !== scope.grant_scope) out.grant_scope = draft.grant_scope;
  const sameGrants = draft.grants.length === scope.grants.length && draft.grants.every((id) => scope.grants.includes(id));
  if (!sameGrants) out.grants = draft.grants;
  if (draft.valid_from !== (scope.valid_from ?? "")) out.valid_from = draft.valid_from || null;
  if (draft.valid_until !== (scope.valid_until ?? "")) out.valid_until = draft.valid_until || null;
  return out;
}

/** The sentences the server will record for this change (governance.describe_changes). */
export function describeChanges(scope: GovernanceAccessScope, draft: ScopeDraft, grantName: (id: number) => string) {
  const lines: string[] = [];
  for (const [field, label] of SCOPE_FLAGS) if (draft[field] !== scope[field]) lines.push(`${label} turned ${draft[field] ? "on" : "off"}`);
  if (draft.grant_scope !== scope.grant_scope) lines.push(`Grants: ${grantScopeLabel(draft.grant_scope).toLowerCase()} (was ${grantScopeLabel(scope.grant_scope).toLowerCase()})`);
  if (draft.grant_scope === "selected") {
    const added = draft.grants.filter((id) => !scope.grants.includes(id)).map(grantName).sort();
    const removed = scope.grants.filter((id) => !draft.grants.includes(id)).map(grantName).sort();
    if (added.length) lines.push(`Grants added: ${added.join(", ")}`);
    if (removed.length) lines.push(`Grants removed: ${removed.join(", ")}`);
  }
  if (draft.valid_from !== (scope.valid_from ?? "")) lines.push(draft.valid_from ? `Access starts ${shortDate(draft.valid_from)}` : "No start date");
  if (draft.valid_until !== (scope.valid_until ?? "")) lines.push(draft.valid_until ? `Access ends ${shortDate(draft.valid_until)}` : "No end date");
  return lines;
}

/** What the form must fix before saving; mirrors GovernanceAccessScopeSerializer.validate. */
export function draftProblems(draft: ScopeDraft) {
  const problems: Record<string, string> = {};
  if (draft.valid_from && draft.valid_until && draft.valid_until < draft.valid_from) problems.valid_until = "The end date cannot come before the start date.";
  if (draft.grant_scope === "selected" && !draft.grants.length) problems.grants = "Tick at least one grant, or choose No grants.";
  return problems;
}

/** The change as the AI context reads it: flags as "true"/"false", grants comma-separated. */
export function draftParams(scope: GovernanceAccessScope, draft: ScopeDraft) {
  const params: Record<string, string | number> = { kind: "scope_change", id: scope.id };
  const changed = changedFields(scope, draft);
  for (const [key, value] of Object.entries(changed)) {
    params[key] = Array.isArray(value) ? value.join(",") : value === null ? "" : String(value);
  }
  return params;
}

// ---- The figures ------------------------------------------------------------------------------

export function budgetStatus(budget: Pick<GovernanceBudget, "start_date" | "end_date" | "utilization_percent">, today: string): Tone {
  if (num(budget.utilization_percent) > 100) return tone("Over budget", "bg-red-500");
  if (budget.start_date > today) return tone("Not started", "bg-gray-400");
  if (budget.end_date < today) return tone("Ended", "bg-gray-400");
  return tone("Running", "bg-emerald-500");
}

export const budgetConfig = (today: string): RegisterConfig<GovernanceBudget> => ({
  search: (row) => `${row.name} ${row.approved_by}`,
  views: [
    { key: "all", label: "All" },
    { key: "running", label: "Running", match: (row) => row.start_date <= today && today <= row.end_date },
    { key: "over", label: "Over budget", match: (row) => num(row.utilization_percent) > 100 },
    { key: "ended", label: "Ended", match: (row) => row.end_date < today },
  ],
  date: { label: "Starts", value: (row) => row.start_date },
  sorts: [
    { key: "used", label: "Most used first", compare: reversed(byNumber((row) => row.utilization_percent)) },
    { key: "start", label: "Newest first", compare: reversed(byText((row) => row.start_date)) },
    { key: "name", label: "Name", compare: byText((row) => row.name) },
  ],
});

export const RESTRICTIONS: Record<string, string> = { unrestricted: "Unrestricted", restricted: "Restricted", temporarily_restricted: "Temporarily restricted", permanently_restricted: "Permanently restricted" };
export const restrictionLabel = (value: string) => RESTRICTIONS[value] ?? value.replaceAll("_", " ");

export function grantStatus(grant: Pick<GovernanceGrant, "status" | "funding_period_end" | "utilization_percent">, today: string): Tone {
  if (grant.status && grant.status !== "active") return tone(grant.status.replaceAll("_", " ").replace(/^./, (c) => c.toUpperCase()), "bg-gray-400");
  if (num(grant.utilization_percent) > 100) return tone("Overspent", "bg-red-500");
  if (grant.funding_period_end < today) return tone("Period ended", "bg-amber-500");
  return tone("Active", "bg-emerald-500");
}

export const grantConfig = (today: string): RegisterConfig<GovernanceGrant> => ({
  search: (row) => `${row.name} ${row.donor} ${row.project ?? ""}`,
  views: [
    { key: "all", label: "All" },
    { key: "active", label: "Funding period open", match: (row) => row.funding_period_start <= today && today <= row.funding_period_end },
    { key: "unspent", label: "Money left", match: (row) => num(row.balance) > 0 },
    { key: "mostly", label: "80% or more spent", match: (row) => num(row.utilization_percent) >= 80 },
  ],
  date: { label: "Funding starts", value: (row) => row.funding_period_start },
  category: { label: "Donor", value: (row) => row.donor },
  sorts: [
    { key: "balance", label: "Most money left", compare: reversed(byNumber((row) => row.balance)) },
    { key: "used", label: "Most spent first", compare: reversed(byNumber((row) => row.utilization_percent)) },
    { key: "name", label: "Name", compare: byText((row) => row.name) },
  ],
});

const DOCUMENTS: Record<string, string> = {
  account: "New account", accountrole: "Account role", journalentry: "Journal entry", vendorbill: "Supplier bill",
  budget: "Budget", creditnote: "Credit note", payrollperiod: "Payroll", payrollsettlement: "Payroll settlement",
  salaryadvance: "Salary advance", salaryadvancedisbursement: "Advance payout", refundrequest: "Refund",
  grant: "Grant", bankreconciliationsignoff: "Bank reconciliation", accountingperiodreopenrequest: "Period reopening",
};
export const documentLabel = (model: string) => DOCUMENTS[model] ?? model.replaceAll("_", " ").replace(/^./, (c) => c.toUpperCase());

export const approvalConfig: RegisterConfig<GovernanceApproval> = {
  search: (row) => `${documentLabel(row.document_type)} ${row.approved_by}`,
  views: [{ key: "all", label: "All" }],
  date: { label: "Approved", value: (row) => row.approved_at },
  category: { label: "Document", value: (row) => documentLabel(row.document_type) },
  sorts: [
    { key: "new", label: "Newest first", compare: reversed(byText((row) => row.approved_at)) },
    { key: "old", label: "Oldest first", compare: byText((row) => row.approved_at) },
    { key: "who", label: "Approved by", compare: byText((row) => row.approved_by) },
  ],
};

export const ACCOUNT_TYPES: Record<string, string> = { asset: "Asset", liability: "Liability", equity: "Fund", income: "Income", expense: "Expense" };

export const accountConfig: RegisterConfig<GovernanceAccountBalance> = {
  search: (row) => `${row.code} ${row.name}`,
  views: [
    { key: "all", label: "All" },
    { key: "moved", label: "With a balance", match: (row) => num(row.balance) !== 0 },
    { key: "main", label: "Main accounts", match: (row) => row.depth === 0 },
  ],
  category: { label: "Type", value: (row) => ACCOUNT_TYPES[row.account_type] ?? row.account_type, inRowOrder: true },
  sorts: [
    { key: "code", label: "Code", compare: byText((row) => row.code) },
    { key: "largest", label: "Largest balance", compare: reversed(byNumber((row) => Math.abs(num(row.balance)))) },
  ],
};

export const payConfig: RegisterConfig<GovernanceStaffPay> = {
  search: (row) => `${row.staff} ${row.position}`,
  views: [
    { key: "all", label: "All" },
    { key: "allowances", label: "With allowances", match: (row) => num(row.allowances) > 0 },
  ],
  category: { label: "Position", value: (row) => row.position },
  sorts: [
    { key: "gross", label: "Highest pay first", compare: reversed(byNumber((row) => row.gross)) },
    { key: "name", label: "Name", compare: byText((row) => row.staff) },
  ],
};

/** The overview's figures as rows, for the one-page export a board paper takes. */
export function overviewRows(data: {
  financial_position: null | { assets: string; liabilities: string; net_assets: string; income: string; expenses: string; surplus_or_deficit: string };
  exceptions: null | { overdue_invoices: number; overdue_amount: string; unreconciled_bank_lines: number; unresolved_provider_rows: number; receivables_control_difference: string | null; payables_control_difference: string | null };
  grants: GovernanceGrant[];
  budgets: null | GovernanceBudget[];
}) {
  const rows: { section: string; figure: string; value: string }[] = [];
  const position = data.financial_position;
  if (position) {
    rows.push(
      { section: "Financial position", figure: "Assets", value: money(position.assets) },
      { section: "Financial position", figure: "Liabilities", value: money(position.liabilities) },
      { section: "Financial position", figure: "Net assets", value: money(position.net_assets) },
      { section: "Financial position", figure: "Income in the period", value: money(position.income) },
      { section: "Financial position", figure: "Expenses in the period", value: money(position.expenses) },
      { section: "Financial position", figure: num(position.surplus_or_deficit) < 0 ? "Deficit" : "Surplus", value: money(Math.abs(num(position.surplus_or_deficit))) },
    );
  }
  const exceptions = data.exceptions;
  if (exceptions) {
    rows.push(
      { section: "Exceptions", figure: "Overdue fee invoices", value: `${exceptions.overdue_invoices} · ${money(exceptions.overdue_amount)}` },
      { section: "Exceptions", figure: "Bank lines not matched", value: String(exceptions.unreconciled_bank_lines) },
      { section: "Exceptions", figure: "Provider rows not resolved", value: String(exceptions.unresolved_provider_rows) },
      { section: "Exceptions", figure: "Receivables control difference", value: exceptions.receivables_control_difference === null ? "Not measured" : money(exceptions.receivables_control_difference) },
      { section: "Exceptions", figure: "Payables control difference", value: exceptions.payables_control_difference === null ? "Not measured" : money(exceptions.payables_control_difference) },
    );
  }
  for (const budget of data.budgets ?? []) rows.push({ section: "Budgets", figure: budget.name, value: `${money(budget.actual_total)} of ${money(budget.budget_total)} (${percent(budget.utilization_percent)})` });
  for (const grant of data.grants) rows.push({ section: "Grants", figure: `${grant.name} (${grant.donor})`, value: `${money(grant.spent)} spent of ${money(grant.received)} received; ${money(grant.balance)} left` });
  return rows;
}
